import { useState, useMemo, useCallback, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AssignmentIcon from '@mui/icons-material/Assignment'
import LanguageIcon from '@mui/icons-material/Language'
import BarChartIcon from '@mui/icons-material/BarChart'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderIcon from '@mui/icons-material/Folder'
import ViewListIcon from '@mui/icons-material/ViewList'
import type { SessionSummary } from '@reachinsight-app/shared/src/components/R3Chat/types'
import { config } from '../config/environment'
import { SERIF_HEADING } from '../theme'

// Per-agent card identity (icon tile tint + pill), matching the approved card design.
const AGENT_META: Record<string, { label: string; tile: string; icon: JSX.Element; keywords: string[] }> = {
  survey_review: { label: 'Authoring', tile: '#e8efe5', icon: <AssignmentIcon sx={{ fontSize: 20, color: '#6b8f5e' }} />, keywords: ['authoring', 'survey', 'ath'] },
  site_builder: { label: 'Website Builder', tile: '#e3edf6', icon: <LanguageIcon sx={{ fontSize: 20, color: '#4a7fb5' }} />, keywords: ['website', 'builder', 'site', 'deliverable', 'mwd'] },
  crosstabs: { label: 'Crosstabs', tile: '#e0f2f5', icon: <BarChartIcon sx={{ fontSize: 20, color: '#008fa8' }} />, keywords: ['crosstabs', 'crosstab', 'tables', 'ct'] },
}
const GENERAL_META = { label: 'General', tile: '#f3efe6', icon: <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: '#a89a78' }} />, keywords: ['general', 'chat'] }

const ARTIFACT_TYPE_LABEL: Record<string, string> = { crosstabs: 'Crosstabs', website: 'Website', survey: 'Survey' }

interface ArtifactVersionRow {
  id: string; version: number; type: string; title: string; client?: string
  sessionId: string; publishedAt: string; bodyR2Key: string
}

function dateGroup(ts: number): string {
  const d = new Date(ts), now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const that = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const days = Math.round((today.getTime() - that.getTime()) / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return 'This week'
  if (days < 31) return 'This month'
  return 'Older'
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function truncId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 4)}...${id.slice(-4)}` : id
}

interface SessionsOverlayProps {
  open: boolean
  onClose: () => void
  sessions: SessionSummary[]
  hasMore: boolean
  onLoadMore: () => void
  onSelectSession: (sessionId: string) => void
  onOpenArtifactVersion: (version: ArtifactVersionRow) => void
  token: string | null
}

/**
 * Full-page sessions overlay: search (titles, ids, agent types, tags — local matches are never
 * dropped by slower server results), two views (Recent by date · Folders by client/domain),
 * copyable session ids, and per-card expandable published artifacts with clickable versions.
 */
export function SessionsOverlay({ open, onClose, sessions, hasMore, onLoadMore, onSelectSession, onOpenArtifactVersion, token }: SessionsOverlayProps) {
  const [query, setQuery] = useState('')
  const [serverResults, setServerResults] = useState<SessionSummary[] | null>(null)
  const [view, setView] = useState<'recent' | 'folders'>('recent')
  const [folderType, setFolderType] = useState<'client' | 'research_domain' | null>(null)
  const [folderValue, setFolderValue] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [artifactsBySession, setArtifactsBySession] = useState<Map<string, ArtifactVersionRow[]>>(new Map())

  const headers = useCallback((): Record<string, string> => {
    const h: Record<string, string> = {}
    if (token) h['Authorization'] = `Bearer ${token}`
    return h
  }, [token])

  // Published artifacts, indexed by owning session (drives the per-card expanders).
  useEffect(() => {
    if (!open) return
    fetch(`${config.API_CF_URL}/api/r3/artifacts`, { credentials: 'include', headers: headers() })
      .then(r => r.ok ? r.json() : null)
      .then((data: { groups?: { type: string; items: { versions: ArtifactVersionRow[] }[] }[] } | null) => {
        const m = new Map<string, ArtifactVersionRow[]>()
        for (const g of data?.groups ?? []) {
          for (const item of g.items) {
            for (const v of item.versions) {
              m.set(v.sessionId, [...(m.get(v.sessionId) ?? []), v])
            }
          }
        }
        setArtifactsBySession(m)
      })
      .catch(() => {})
  }, [open, headers])

  // Server search SUPPLEMENTS local matching (it searches message contents the FE doesn't have).
  // Local matches are never dropped when the server returns less/nothing — that was the
  // "results flash then vanish" bug.
  useEffect(() => {
    if (!open || query.trim().length < 2) { setServerResults(null); return }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${config.API_CF_URL}/api/r3/sessions/search?q=${encodeURIComponent(query)}`, { credentials: 'include', headers: headers() })
        if (res.ok) setServerResults(((await res.json()) as { sessions: SessionSummary[] }).sessions ?? [])
      } catch { setServerResults(null) }
    }, 300)
    return () => clearTimeout(t)
  }, [query, open, headers])

  // Folder values present in the loaded sessions.
  const folders = useMemo(() => {
    const out: Record<'client' | 'research_domain', Map<string, number>> = { client: new Map(), research_domain: new Map() }
    for (const s of sessions) {
      for (const t of ['client', 'research_domain'] as const) {
        const v = s.tags?.[t]
        if (v) out[t].set(v, (out[t].get(v) ?? 0) + 1)
      }
    }
    return out
  }, [sessions])

  const matchesQuery = useCallback((s: SessionSummary, q: string): boolean => {
    const meta = AGENT_META[s.panelType ?? ''] ?? GENERAL_META
    return (s.intentSummary ?? '').toLowerCase().includes(q)
      || s.id.toLowerCase().includes(q)
      || meta.label.toLowerCase().includes(q)
      || meta.keywords.some(k => k.startsWith(q) || q.includes(k))
      || Object.values(s.tags ?? {}).some(v => v.toLowerCase().includes(q))
  }, [])

  const visible = useMemo(() => {
    let list = sessions
    const q = query.trim().toLowerCase()
    if (q) {
      const local = sessions.filter(s => matchesQuery(s, q))
      // UNION with server results (message-content hits), local first, dedup by id.
      const seen = new Set(local.map(s => s.id))
      const extra = (serverResults ?? []).filter(s => !seen.has(s.id))
      list = [...local, ...extra]
    }
    if (folderType && folderValue) {
      const tagById = new Map(sessions.map(s => [s.id, s.tags]))
      list = list.filter(s => (s.tags ?? tagById.get(s.id))?.[folderType] === folderValue)
    }
    return list
  }, [sessions, serverResults, query, folderType, folderValue, matchesQuery])

  const dateGroups = useMemo(() => {
    const m = new Map<string, SessionSummary[]>()
    for (const s of visible) m.set(dateGroup(s.startedAt), [...(m.get(dateGroup(s.startedAt)) ?? []), s])
    return [...m.entries()]
  }, [visible])

  // Folder view: client folders → domain folders → untagged bucket.
  const folderGroups = useMemo(() => {
    const buckets = new Map<string, { kind: 'client' | 'domain' | 'none'; label: string; items: SessionSummary[] }>()
    for (const s of visible) {
      const client = s.tags?.client
      const domain = s.tags?.research_domain
      const key = client ? `c:${client}` : domain ? `d:${domain}` : 'untagged'
      const b = buckets.get(key) ?? {
        kind: client ? 'client' as const : domain ? 'domain' as const : 'none' as const,
        label: client ?? domain ?? 'Untagged',
        items: [],
      }
      b.items.push(s)
      buckets.set(key, b)
    }
    return [...buckets.entries()].sort((a, b) => (a[1].kind === 'none' ? 1 : 0) - (b[1].kind === 'none' ? 1 : 0))
  }, [visible])

  const pick = useCallback((id: string) => { onSelectSession(id); onClose() }, [onSelectSession, onClose])

  const copyId = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id)
      setTimeout(() => setCopiedId(c => c === id ? null : c), 1400)
    }).catch(() => {})
  }, [])

  const toggleExpand = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  if (!open) return null

  const renderCard = (s: SessionSummary) => {
    const meta = AGENT_META[s.panelType ?? ''] ?? GENERAL_META
    const arts = artifactsBySession.get(s.id) ?? []
    const isExpanded = expanded.has(s.id)
    return (
      <Box key={s.id} sx={{ mb: 1.5 }}>
        <Box onClick={() => pick(s.id)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 2,
            background: '#fff', border: '1px solid #eceae3', cursor: 'pointer',
            borderRadius: isExpanded && arts.length ? '16px 16px 0 0' : '16px',
            transition: 'all .12s', '&:hover': { borderColor: '#d8d4c8', boxShadow: '0 2px 10px rgba(0,0,0,.05)' },
          }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: meta.tile, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {meta.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.98rem', fontWeight: 600, color: '#2c2c2c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.intentSummary || 'New conversation'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.4, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#a09a8d' }}>{fmtTime(s.startedAt)}</Typography>
              <Tooltip title={copiedId === s.id ? 'Copied!' : `${s.id} — click to copy`}>
                <Typography onClick={(e) => copyId(e, s.id)}
                  sx={{
                    fontSize: '0.72rem', color: copiedId === s.id ? '#2e8b5c' : '#c5c0b4',
                    fontFamily: '"SF Mono","Fira Code",monospace', cursor: 'copy', display: 'inline-flex', alignItems: 'center', gap: 0.4,
                    '&:hover': { color: '#857f70' },
                  }}>
                  {copiedId === s.id ? 'copied ✓' : truncId(s.id)}<ContentCopyIcon sx={{ fontSize: 11 }} />
                </Typography>
              </Tooltip>
              <Box sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#857f70', background: '#f0ede4', px: 1, py: 0.2, borderRadius: '999px' }}>{meta.label}</Box>
              {s.tags?.client && <Box sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#008fa8', background: '#008fa814', px: 1, py: 0.2, borderRadius: '999px' }}>{s.tags.client}</Box>}
              {s.tags?.research_domain && <Box sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#7c3aed', background: '#7c3aed14', px: 1, py: 0.2, borderRadius: '999px' }}>{s.tags.research_domain}</Box>}
              {arts.length > 0 && (
                <Box onClick={(e) => toggleExpand(e, s.id)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.3, fontSize: '0.7rem', fontWeight: 700,
                    color: '#0a6f82', background: '#f0fafc', border: '1px solid #bfe6ee', px: 1, py: 0.2, borderRadius: '999px', cursor: 'pointer',
                    '&:hover': { background: '#e0f2f5' },
                  }}>
                  {arts.length} artifact{arts.length > 1 ? 's' : ''}
                  <ExpandMoreIcon sx={{ fontSize: 13, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                </Box>
              )}
            </Box>
          </Box>
          <ArrowForwardIcon sx={{ fontSize: 18, color: '#c5c0b4' }} />
        </Box>
        {isExpanded && arts.length > 0 && (
          <Box sx={{ background: '#fbfaf6', border: '1px solid #eceae3', borderTop: 'none', borderRadius: '0 0 16px 16px', px: 2.5, py: 1.25 }}>
            {Object.entries(arts.reduce<Record<string, ArtifactVersionRow[]>>((acc, v) => {
              const k = `${v.type}:${v.id}`
              ;(acc[k] ??= []).push(v)
              return acc
            }, {})).map(([k, versions]) => {
              const sorted = [...versions].sort((a, b) => b.version - a.version)
              return (
                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#57524a' }}>
                    {ARTIFACT_TYPE_LABEL[sorted[0].type] ?? sorted[0].type}: {sorted[0].title}
                  </Typography>
                  {sorted.map(v => (
                    <Box key={v.version}
                      onClick={(e) => { e.stopPropagation(); onOpenArtifactVersion(v); onClose() }}
                      sx={{
                        fontSize: '0.66rem', fontWeight: 700, px: 0.8, py: 0.15, borderRadius: '5px', cursor: 'pointer',
                        color: '#0a6f82', background: '#f0fafc', border: '1px solid #bfe6ee',
                        '&:hover': { background: '#008fa8', color: '#fff' },
                      }}>
                      v{v.version}
                    </Box>
                  ))}
                </Box>
              )
            })}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, background: '#faf9f5', display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 4, py: 2, borderBottom: '1px solid #e8e5de', background: '#fff' }}>
        <Typography sx={{ ...SERIF_HEADING, fontSize: '1.15rem', color: '#2c2c2c' }}>
          Your conversations
        </Typography>
        <TextField
          size="small" autoFocus value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search by title, id, agent (authoring, crosstabs…), or tag"
          sx={{ width: 460, '& .MuiInputBase-root': { background: '#faf9f5', borderRadius: '10px' } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#aaa' }} /></InputAdornment> }}
        />
        {/* view toggle: Recent (date order) | Folders (client/domain) */}
        <Box sx={{ display: 'flex', border: '1px solid #e0ddd3', borderRadius: '10px', overflow: 'hidden' }}>
          {([['recent', 'Recent', <ViewListIcon key="r" sx={{ fontSize: 15 }} />], ['folders', 'Folders', <FolderIcon key="f" sx={{ fontSize: 14 }} />]] as const).map(([id, label, icon]) => (
            <Box key={id} onClick={() => setView(id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.6, px: 1.5, py: 0.7, cursor: 'pointer',
                fontSize: '0.78rem', fontWeight: 600,
                color: view === id ? '#fff' : '#857f70', background: view === id ? '#6b8f5e' : '#fff',
              }}>
              {icon}{label}
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {/* folder filter chips (active in either view) */}
      {(folders.client.size > 0 || folders.research_domain.size > 0) && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 4, py: 1.25, borderBottom: '1px solid #f0eeea', flexWrap: 'wrap', background: '#fff' }}>
          {([['client', '#008fa8', 'Clients'], ['research_domain', '#7c3aed', 'Domains']] as const).map(([type, color, label]) =>
            folders[type].size > 0 && (
              <Box key={type} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                {[...folders[type].entries()].map(([value, count]) => {
                  const active = folderType === type && folderValue === value
                  return (
                    <Box key={value}
                      onClick={() => { setFolderType(active ? null : type); setFolderValue(active ? null : value) }}
                      sx={{
                        fontSize: '0.72rem', fontWeight: 600, px: 1.1, py: 0.3, borderRadius: '999px', cursor: 'pointer',
                        color: active ? '#fff' : color, background: active ? color : `${color}14`,
                        '&:hover': { background: active ? color : `${color}26` },
                      }}>
                      {value} <span style={{ opacity: 0.65 }}>({count})</span>
                    </Box>
                  )
                })}
              </Box>
            ))}
        </Box>
      )}

      {/* body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
          {visible.length === 0 && (
            <Typography sx={{ color: '#999', textAlign: 'center', py: 6 }}>No conversations match.</Typography>
          )}
          {view === 'recent' && dateGroups.map(([group, items]) => (
            <Box key={group} sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>{group}</Typography>
              {items.map(renderCard)}
            </Box>
          ))}
          {view === 'folders' && folderGroups.map(([key, bucket]) => {
            const color = bucket.kind === 'client' ? '#008fa8' : bucket.kind === 'domain' ? '#7c3aed' : '#999'
            return (
              <Box key={key} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <FolderIcon sx={{ fontSize: 15, color }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {bucket.label} <span style={{ opacity: 0.6, fontWeight: 600 }}>({bucket.items.length})</span>
                  </Typography>
                </Box>
                {bucket.items.map(renderCard)}
              </Box>
            )
          })}
          {hasMore && !query && (
            <Typography onClick={onLoadMore}
              sx={{ textAlign: 'center', py: 2, fontSize: '0.8rem', fontWeight: 600, color: '#6b8f5e', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              Load older conversations
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}
